<script setup lang="ts">
/**
 * design-system.md -> Components -> Avatar and tile. People are round. Initials come from `email`:
 * two letters, one from each of the first two `.`/`_`/`-`-separated parts of the local part (before
 * the @), or the first two letters of the local part if it has no separator. `pending` renders the
 * invite state instead — a dashed outline with a mail glyph, no initials (design-system.md ->
 * Identity: "invited").
 *
 * `size` in px (default 36 — the standard list/table size; the rail's signed-in-user block uses
 * 32). `tone` picks the fill for the initials state: "ink" (default, on a light surface) or "rail"
 * (`rail-tile` fill — use this on the dark nav rail).
 */
import { computed } from 'vue';
import { Mail } from '@lucide/vue';

const props = withDefaults(defineProps<{ email: string; pending?: boolean; size?: number; tone?: 'ink' | 'rail' }>(), {
  pending: false,
  size: 36,
  tone: 'ink'
});

const initials = computed(() => {
  const local = props.email.split('@')[0] ?? '';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  return local.slice(0, 2).toUpperCase();
});
</script>

<template>
  <span
    aria-hidden="true"
    class="flex shrink-0 items-center justify-center rounded-full text-[12.5px] font-semibold"
    :class="pending ? 'border border-dashed border-line-strong bg-surface text-ink-3' : tone === 'rail' ? 'bg-rail-tile text-rail-text' : 'bg-ink text-on-ink'"
    :style="{ width: `${size}px`, height: `${size}px` }"
  >
    <Mail v-if="pending" :size="16" :stroke-width="1.75" />
    <template v-else>{{ initials }}</template>
  </span>
</template>
